import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

/**
 * Native-shell behaviour for the Capacitor Android/iOS build.
 *
 * Everything here is inert on the web: `Capacitor.isNativePlatform()` is false
 * in a normal browser, so the listeners are never attached and the app behaves
 * exactly as it does today. That keeps one codebase serving both.
 */

/** True only inside the Capacitor native shell, not in a mobile browser. */
export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Whether a URL points somewhere outside the app.
 *
 * In-app routes are relative ("/Settings"); anything with a scheme and a
 * different origin is external and must not be loaded inside the WebView,
 * where it would strand the user with no browser chrome to escape with.
 */
function isExternalUrl(url: string): boolean {
  if (!url) return false;
  if (url.startsWith("/") && !url.startsWith("//")) return false;
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Open a URL outside the app (system browser / Custom Tab), falling back to a
 * normal new tab on the web.
 *
 * This matters for the shop and checkout links: inside a WebView they would
 * otherwise replace the app with a web page the user cannot navigate back from.
 */
export async function openExternal(url: string): Promise<void> {
  if (isNative()) {
    await Browser.open({ url });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Route external links through the system browser.
 *
 * Done with one delegated listener rather than by editing every link, so links
 * added later are covered automatically and the web build is untouched.
 * Returns a cleanup function.
 */
export function interceptExternalLinks(): () => void {
  if (!isNative()) return () => {};

  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    const anchor = (event.target as HTMLElement | null)?.closest?.("a");
    const href = anchor?.getAttribute("href");
    if (!anchor || !href) return;
    // Leave mailto:, tel: and the like to the OS.
    if (/^(mailto|tel|sms):/i.test(href)) return;
    if (!isExternalUrl(href)) return;

    event.preventDefault();
    void openExternal(new URL(href, window.location.href).toString());
  };

  document.addEventListener("click", onClick);
  return () => document.removeEventListener("click", onClick);
}

/** Leave the app, as Android expects when back is pressed at the root. */
export async function exitApp(): Promise<void> {
  if (!isNative()) return;
  await CapApp.exitApp();
}

/**
 * Wire the Android hardware back button to a handler.
 *
 * Deliberately ignores the event's `canGoBack` flag. That flag is the native
 * WebView's own navigation history, and react-router does NOT drive it: routing
 * happens through history.pushState, which the WebView does not count. So
 * canGoBack is false on every screen of this app, and trusting it made back
 * close the app from anywhere, which is the bug it was meant to prevent. It was
 * invisible until the app ran on a real Android build.
 *
 * Deciding whether a back is possible needs router state, so that call belongs
 * to the caller (see useNativeShell); this only delivers the press.
 */
export function handleBackButton(onBack: () => void): () => void {
  if (!isNative()) return () => {};

  const listener = CapApp.addListener("backButton", () => {
    onBack();
  });

  return () => {
    void listener.then((handle) => handle.remove());
  };
}
